import { useCallback, useEffect, useState } from 'react';
import { useBotRoute } from '../lib/useBotRoute';
import { hubClient, type Form } from '../services/hubClient';
import { FormEditor } from './FormEditor';

function completionLabel(form: Form): string {
  const c = form.completion;
  if (c?.tool) return `tool: ${c.tool}`;
  if (c?.workflowId) return `workflow: ${c.workflowId}`;
  if (c?.utter) return 'utter';
  return '—';
}

export function FormsBuilder() {
  const { system, botId } = useBotRoute();
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await hubClient.listForms(system, botId);
      setForms(list);
      // Keep selection if still present; otherwise clear.
      setSelectedId((cur) => (cur && list.some((f) => f.id === cur) ? cur : null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, [system, botId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function createForm() {
    if (!newName.trim()) return;
    setError(null);
    try {
      const created = await hubClient.createForm({ system, botId, name: newName.trim() });
      setNewName('');
      setCreating(false);
      await load();
      setSelectedId(created.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create form');
    }
  }

  const active = forms.filter((f) => f.status !== 'D');

  return (
    <div className="flex h-full min-h-0">
      {/* List pane */}
      <div className="flex w-80 flex-col border-r border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div>
            <h1 className="text-sm font-semibold">Forms</h1>
            <p className="text-xs text-gray-400">
              {system} / {botId}
            </p>
          </div>
          <button
            onClick={() => setCreating((v) => !v)}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white"
          >
            New
          </button>
        </div>

        {creating && (
          <div className="border-b border-gray-200 bg-gray-50 p-3">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createForm()}
              placeholder="Form name"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <div className="mt-2 flex justify-end gap-2">
              <button
                onClick={() => {
                  setCreating(false);
                  setNewName('');
                }}
                className="rounded px-3 py-1 text-sm text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={createForm}
                disabled={!newName.trim()}
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-40"
              >
                Create
              </button>
            </div>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading && <p className="p-4 text-sm text-gray-400">Loading…</p>}
          {error && <p className="m-3 rounded bg-red-50 p-2 text-xs text-red-700">{error}</p>}
          {!loading &&
            active.map((f) => (
              <button
                key={f.id}
                onClick={() => setSelectedId(f.id)}
                className={`block w-full border-b border-gray-100 px-4 py-3 text-left ${
                  selectedId === f.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900">{f.name}</div>
                <div className="text-xs text-gray-400">{completionLabel(f)}</div>
              </button>
            ))}
          {!loading && !error && active.length === 0 && (
            <p className="p-4 text-sm text-gray-400">
              No forms for this bot yet. Create one to start collecting slots.
            </p>
          )}
        </div>
      </div>

      {/* Detail pane */}
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
        {selectedId ? (
          <FormEditor
            key={selectedId}
            formId={selectedId}
            onChanged={load}
            onDeleted={() => {
              setSelectedId(null);
              void load();
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-gray-400">
            Select a form, or create a new one.
          </div>
        )}
      </div>
    </div>
  );
}
